import React, { useState, useCallback, useEffect, useRef } from 'react';
import { IS_PRERENDER } from '../utils/prerender';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import {
  sendSupportMessage, supportCooldownLeft,
  SUPPORT_MESSAGE_MAX, SUPPORT_CONTACT_MAX,
} from '../firebase/supportService';
import { CONTACT } from '../i18n/legal';
import './SupportWidget.css';

// ── CONTACT US, FOR VISITORS WITHOUT AN ACCOUNT ──────────────────────────────
// A button in the bottom-right corner, opening a short form that files a
// message for the admins. See firebase/supportService.
//
// ONLY WHEN SIGNED OUT. A signed-in user has Messages, so support for them is a
// real conversation with the admin account — opened by "Contact admin" at the
// bottom of the conversation list (ChatUIContext), answered in the admin's own
// Messages, with history and replies. This form cannot be replied to at all;
// it lands in the admin panel and is answered by phone or email.
//
// So the two are not two versions of the same thing. This one exists because
// someone who cannot sign in is precisely who most needs to reach support —
// which is also why the rules allow an unauthenticated create, and why they
// validate it so tightly.
//
// PORTALLED TO THE BODY. It has to sit above whatever is on screen, and the app
// has several stacking contexts that would otherwise trap it — the chat
// overlay, the 3D editor's panels, every modal. Rendered in place it would
// disappear under one of them.
//
// WHERE IT IS STILL NOT SHOWN, even signed out: the builder (/try), where the
// zoom controls and the wall-height pill already own that corner and a floating
// button sits on top of the canvas. The other three paths in HIDE_ON are
// signed-in routes, kept because a session can end while one is open.

const HIDE_ON = [/^\/workspace/, /^\/app\//, /^\/try/, /^\/admin/];

function LifebuoyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

export default function SupportWidget() {
  const { t } = useT();
  const { user, profile } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [wait, setWait] = useState(0);
  const boxRef = useRef(null);

  // Close on Escape, like every other dialog in the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Opening reads the cooldown once — a live countdown would re-render the
  // form on every tick while somebody is typing into it.
  useEffect(() => {
    if (open) {
      setWait(Math.ceil(supportCooldownLeft() / 1000));
      setSent(false);
      setError('');
    }
  }, [open]);

  const submit = useCallback(async (e) => {
    e?.preventDefault();
    const body = message.trim();
    if (!body || busy) return;

    const left = supportCooldownLeft();
    if (left > 0) {
      setWait(Math.ceil(left / 1000));
      setError(t('sup_err_wait').replace('{s}', String(Math.ceil(left / 1000))));
      return;
    }

    setBusy(true);
    setError('');
    try {
      await sendSupportMessage({
        message: body,
        contact: contact.trim(),
        name: profile?.fullName || user?.displayName || '',
        // PATH ONLY. The query string can carry ids, search terms and share
        // tokens, none of which anyone consented to file with a support
        // ticket. The path alone is enough to know where they were.
        page: location.pathname,
      });
      setSent(true);
      setMessage('');
      setContact('');
    } catch (err) {
      // A rules rejection arrives as `permission-denied`, which for this form
      // means the payload failed validation — worth saying differently from a
      // network failure, because retrying will not help.
      setError(err?.code === 'permission-denied' ? t('sup_err_rejected') : t('sup_err_failed'));
    } finally {
      setBusy(false);
    }
  }, [message, contact, busy, profile, user, location.pathname, t]);

  // ── SIGNED OUT ONLY ────────────────────────────────────────────────────────
  // A signed-in user reaches support through Messages instead — a real
  // conversation with the admin account, with history and replies, opened by
  // the "Contact admin" button at the bottom of the conversation list (see
  // ChatUIContext). That is strictly better than this form: it can be answered.
  //
  // So this is now for people who have no Messages to open. Their message lands
  // in `supportMessages` and is read in the admin panel, where it is answered
  // by phone or email — which is why the panel shows whatever contact detail
  // they left.
  //
  // Two support surfaces that behaved differently was the thing to avoid, not
  // the number of them.
  if (user) return null;
  if (HIDE_ON.some(re => re.test(location.pathname))) return null;

  const left = SUPPORT_MESSAGE_MAX - message.length;

  // Same reason as LanguageModal: portalled markup captured by react-snap is
  // never hydrated, so the prerendered copy would sit dead on top of the real
  // one. See utils/prerender.
  if (IS_PRERENDER) return null;

  return createPortal(
    <>
      <button
        type="button"
        className={`sup-fab${open ? ' sup-fab--open' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-label={t('nav_support')}
        title={t('nav_support')}
      >
        {open ? <span className="sup-fab-x" aria-hidden="true">✕</span> : <LifebuoyIcon />}
      </button>

      {open && (
        <div className="sup-panel" role="dialog" aria-modal="false"
          aria-label={t('nav_support')} ref={boxRef}>
          <header className="sup-head">
            <h2 className="sup-title">{t('nav_support')}</h2>
            <p className="sup-sub">{t('sup_sub')}</p>
          </header>

          {sent ? (
            /* Not a toast that vanishes: the reader needs to know it left, and
               the phone numbers are the faster route if it is urgent. */
            <div className="sup-done">
              <div className="sup-done-tick" aria-hidden="true">✓</div>
              <p className="sup-done-text">{t('sup_done')}</p>
              <div className="sup-phones">
                {CONTACT.phones.map((p, i) => (
                  <a key={p} className="sup-phone" href={`tel:${CONTACT.phonesHref[i]}`}>{p}</a>
                ))}
              </div>
              <button type="button" className="sup-btn sup-btn--ghost" onClick={() => setOpen(false)}>
                {t('close')}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <label className="sup-field">
                <span className="sup-label">{t('sup_message')}</span>
                <textarea
                  className="sup-textarea"
                  value={message}
                  onChange={(e) => { setMessage(e.target.value.slice(0, SUPPORT_MESSAGE_MAX)); setError(''); }}
                  placeholder={t('sup_message_ph')}
                  rows={4}
                  maxLength={SUPPORT_MESSAGE_MAX}
                  autoFocus
                />
                {/* Only once it is close enough to matter — a counter at 1,900
                    remaining is noise. */}
                {left < 200 && <span className="sup-count">{left}</span>}
              </label>

              {/* ALWAYS ASKED, and no longer conditional on being signed out —
                  this widget only renders for a signed-out visitor now, so
                  there is never an account to take a reply address from. It is
                  also the only way an answer can reach them: their message
                  lands in the admin panel, which has no thread to reply in. */}
              <label className="sup-field">
                <span className="sup-label">{t('sup_contact')}</span>
                <input
                  className="sup-input"
                  value={contact}
                  onChange={(e) => setContact(e.target.value.slice(0, SUPPORT_CONTACT_MAX))}
                  placeholder={t('sup_contact_ph')}
                  maxLength={SUPPORT_CONTACT_MAX}
                />
              </label>

              {error && <p className="sup-error" role="alert">{error}</p>}
              {!error && wait > 0 && <p className="sup-hint">{t('sup_err_wait').replace('{s}', String(wait))}</p>}

              <button className="sup-btn sup-btn--primary" type="submit"
                disabled={busy || !message.trim()}>
                {busy ? t('sup_sending') : t('sup_send')}
              </button>

              <p className="sup-or">{t('sup_or_call')}</p>
              <div className="sup-phones">
                {CONTACT.phones.map((p, i) => (
                  <a key={p} className="sup-phone" href={`tel:${CONTACT.phonesHref[i]}`}>{p}</a>
                ))}
              </div>
            </form>
          )}
        </div>
      )}
    </>,
    document.body,
  );
}
